import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { 
  Download, 
  ArrowBack, 
  People, 
  Loyalty, 
  ShoppingCart, 
  AttachMoney,
  Refresh,
  TrendingUp,
  TrendingDown,
  Info
} from '@mui/icons-material';
import reportService from '../../../services/reportService';
import excelExport from '../../../utils/excelExport';
import DateRangeFilter from './DateRangeFilter';
import './CustomerReports.css';

const CustomerReports = ({ onPageChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('customer_analytics');
  const [reportData, setReportData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  const reportTypes = [
    { 
      value: 'customer_analytics', 
      label: 'Customer Analytics', 
      icon: <People />,
      description: 'Comprehensive customer insights and trends'
    },
    { 
      value: 'loyal_customers', 
      label: 'Top Customers', 
      icon: <Loyalty />,
      description: 'Highest value and most loyal customers'
    },
    { 
      value: 'customer_segments', 
      label: 'Customer Segments', 
      icon: <ShoppingCart />,
      description: 'Customer segmentation by behavior and spending'
    }
  ];

  // Color schemes
  const chartColors = {
    primary: '#6366F1',
    secondary: '#10B981',
    accent: '#F59E0B',
    warning: '#EF4444',
    info: '#3B82F6',
    success: '#059669'
  };

  useEffect(() => {
    loadReportData();
  }, [reportType, filters]);

  const loadReportData = async () => {
    setLoading(true);
    setError('');
    try {
      let response;
      switch (reportType) {
        case 'customer_analytics':
        case 'loyal_customers':
        case 'customer_segments':
          response = await reportService.getCustomerAnalytics(filters);
          break;
        default:
          response = await reportService.getCustomerAnalytics(filters);
      }
      setReportData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load customer report data: ' + (err.message || 'Unknown error'));
      console.error('Customer report loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!reportData) return;
    
    try {
      const exportData = reportData.top_customers?.map(customer => ({
        'Customer Name': customer.name,
        'Email': customer.email,
        'Phone': customer.phone,
        'Total Orders': customer.order_count,
        'Total Spent': customer.total_spent,
        'Average Order Value': customer.average_order_value,
        'Last Order Date': customer.last_order_date
      })) || [];

      if (exportData.length > 0) {
        excelExport.exportToExcel(exportData, 'Customer_Analytics_Report');
      } else {
        setError('No customer data available for export');
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data: ' + err.message);
    }
  };

  const renderMetricCard = (title, value, subtitle = '', format = 'number', color = 'primary', trend = null) => {
    const formattedValue = format === 'currency' 
      ? `LKR ${value?.toLocaleString() || '0'}` 
      : value?.toLocaleString() || '0';

    return (
      <Card className="cust-metric-card">
        <CardContent className="cust-metric-content">
          <div className="cust-metric-header">
            <Typography className="cust-metric-title">
              {title}
            </Typography>
            {subtitle && (
              <Tooltip title={subtitle}>
                <Info className="cust-metric-info" />
              </Tooltip>
            )}
          </div>
          <Typography className={`cust-metric-value cust-metric-${color}`}>
            {formattedValue}
          </Typography>
          {trend && (
            <div className={`cust-metric-trend cust-trend-${trend.direction}`}>
              {trend.direction === 'up' ? (
                <TrendingUp className="cust-trend-icon" />
              ) : trend.direction === 'down' ? (
                <TrendingDown className="cust-trend-icon" />
              ) : null}
              <Typography className="cust-trend-text">
                {trend.value}% {trend.direction === 'up' ? 'increase' : 'decrease'}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCustomerAnalytics = () => {
    if (!reportData?.top_customers) return null;

    // Customer spending segments
    const spendingSegments = [
      { range: 'LKR 0-1k', count: reportData.top_customers.filter(c => c.total_spent < 1000).length },
      { range: 'LKR 1k-5k', count: reportData.top_customers.filter(c => c.total_spent >= 1000 && c.total_spent < 5000).length },
      { range: 'LKR 5k-10k', count: reportData.top_customers.filter(c => c.total_spent >= 5000 && c.total_spent < 10000).length },
      { range: 'LKR 10k+', count: reportData.top_customers.filter(c => c.total_spent >= 10000).length }
    ];

    const COLORS = [chartColors.primary, chartColors.secondary, chartColors.accent, chartColors.warning];

    return (
      <div className="cust-report-container">
        {/* Summary Metrics */}
        <div className="cust-metrics-grid">
          {renderMetricCard(
            'Total Customers',
            reportData.total_customers,
            'Total number of customers',
            'number',
            'primary'
          )}
          {renderMetricCard(
            'New Customers',
            reportData.new_customers_count,
            'New customers in selected period',
            'number',
            'success'
          )}
          {renderMetricCard(
            'Avg Orders/Customer',
            reportData.top_customers.length > 0 
              ? (reportData.top_customers.reduce((sum, c) => sum + c.order_count, 0) / reportData.top_customers.length).toFixed(1)
              : 0,
            'Average orders per customer',
            'number',
            'info'
          )}
          {renderMetricCard(
            'Avg Spend/Customer',
            reportData.top_customers.length > 0 
              ? (reportData.top_customers.reduce((sum, c) => sum + c.total_spent, 0) / reportData.top_customers.length).toFixed(0)
              : 0,
            'Average spending per customer',
            'currency',
            'warning'
          )}
        </div>

        {/* Charts Row */}
        <div className="cust-charts-row">
          <Card className="cust-chart-card">
            <CardContent className="cust-chart-content">
              <div className="cust-chart-header">
                <People className="cust-chart-icon" />
                <Typography className="cust-chart-title">Customer Spending Segments</Typography>
              </div>
              <div className="cust-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart className="cust-pie-chart">
                    <Pie
                      data={spendingSegments}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ range, count }) => `${range}: ${count}`}
                      outerRadius={100}
                      dataKey="count"
                    >
                      {spendingSegments.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip className="cust-chart-tooltip" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="cust-chart-card">
            <CardContent className="cust-chart-content">
              <div className="cust-chart-header">
                <TrendingUp className="cust-chart-icon" />
                <Typography className="cust-chart-title">Customer Growth Trend</Typography>
              </div>
              <div className="cust-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={reportData.new_customers_trend} className="cust-line-chart">
                    <CartesianGrid className="cust-chart-grid" />
                    <XAxis dataKey="date" className="cust-chart-axis" />
                    <YAxis className="cust-chart-axis" />
                    <RechartsTooltip className="cust-chart-tooltip" />
                    <Legend className="cust-chart-legend" />
                    <Line 
                      type="monotone" 
                      dataKey="new_customers" 
                      stroke={chartColors.primary} 
                      name="New Customers" 
                      strokeWidth={3}
                      dot={{ fill: chartColors.primary, strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderTopCustomers = () => {
    if (!reportData?.top_customers) return null;

    const topSpenders = reportData.top_customers.slice(0, 10);

    return (
      <div className="cust-report-container">
        {/* Summary Metrics */}
        <div className="cust-metrics-grid">
          {renderMetricCard(
            'Top Customers',
            topSpenders.length,
            'Number of top customers analyzed',
            'number',
            'primary'
          )}
          {renderMetricCard(
            'Total Revenue',
            topSpenders.reduce((sum, c) => sum + c.total_spent, 0),
            'Total revenue from top customers',
            'currency',
            'success'
          )}
          {renderMetricCard(
            'Avg Order Value',
            topSpenders.reduce((sum, c) => sum + c.average_order_value, 0) / topSpenders.length,
            'Average order value among top customers',
            'currency',
            'info'
          )}
          {renderMetricCard(
            'Total Orders',
            topSpenders.reduce((sum, c) => sum + c.order_count, 0),
            'Total orders from top customers',
            'number',
            'warning'
          )}
        </div>

        {/* Charts and Table */}
        <div className="cust-charts-row">
          <Card className="cust-chart-card cust-chart-wide">
            <CardContent className="cust-chart-content">
              <div className="cust-chart-header">
                <Loyalty className="cust-chart-icon" />
                <Typography className="cust-chart-title">Top Customers by Spending</Typography>
              </div>
              <div className="cust-chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={topSpenders} className="cust-bar-chart">
                    <CartesianGrid className="cust-chart-grid" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} className="cust-chart-axis" />
                    <YAxis className="cust-chart-axis" />
                    <RechartsTooltip 
                      formatter={(value) => [`LKR ${value.toLocaleString()}`, 'Amount']}
                      className="cust-chart-tooltip"
                    />
                    <Legend className="cust-chart-legend" />
                    <Bar dataKey="total_spent" fill={chartColors.primary} name="Total Spent" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="average_order_value" fill={chartColors.secondary} name="Avg Order Value" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="cust-table-card">
            <CardContent className="cust-table-content">
              <div className="cust-table-header">
                <People className="cust-table-icon" />
                <Typography className="cust-table-title">Customer Leaderboard</Typography>
              </div>
              <TableContainer className="cust-table-container">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="cust-table-header-cell">Customer</TableCell>
                      <TableCell align="right" className="cust-table-header-cell">Orders</TableCell>
                      <TableCell align="right" className="cust-table-header-cell">Total Spent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topSpenders.map((customer, index) => (
                      <TableRow key={customer.id} className="cust-table-row">
                        <TableCell>
                          <div className="cust-customer-info">
                            <Avatar className="cust-customer-avatar">
                              {customer.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <div className="cust-customer-details">
                              <Typography className="cust-customer-name">{customer.name}</Typography>
                              <Typography className="cust-customer-email">{customer.email}</Typography>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={customer.order_count} 
                            className="cust-order-chip"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography className="cust-spent-amount">
                            LKR {customer.total_spent?.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderCustomerSegments = () => {
    if (!reportData?.top_customers) return null;

    // Segment customers by order frequency
    const segments = [
      { 
        segment: 'VIP Customers', 
        criteria: '10+ orders', 
        customers: reportData.top_customers.filter(c => c.order_count >= 10),
        color: 'primary'
      },
      { 
        segment: 'Regular Customers', 
        criteria: '3-9 orders', 
        customers: reportData.top_customers.filter(c => c.order_count >= 3 && c.order_count < 10),
        color: 'success'
      },
      { 
        segment: 'Occasional Customers', 
        criteria: '1-2 orders', 
        customers: reportData.top_customers.filter(c => c.order_count >= 1 && c.order_count < 3),
        color: 'warning'
      },
      { 
        segment: 'New Customers', 
        criteria: '1 order', 
        customers: reportData.top_customers.filter(c => c.order_count === 1),
        color: 'info'
      }
    ];

    return (
      <div className="cust-report-container">
        {/* Summary Metrics */}
        <div className="cust-metrics-grid">
          {segments.map((segment, index) => (
            <div key={segment.segment} className="cust-metric-item">
              {renderMetricCard(
                segment.segment,
                segment.customers.length,
                segment.criteria,
                'number',
                segment.color
              )}
            </div>
          ))}
        </div>

        {/* Customer Segments */}
        <div className="cust-segments-grid">
          {segments.map((segment, index) => (
            <Card key={segment.segment} className="cust-segment-card">
              <CardContent className="cust-segment-content">
                <div className="cust-segment-header">
                  <div className={`cust-segment-indicator cust-${segment.color}`} />
                  <Typography className="cust-segment-title">{segment.segment}</Typography>
                </div>
                <Typography className="cust-segment-criteria">
                  {segment.criteria} • {segment.customers.length} customers
                </Typography>
                
                <div className="cust-segment-customers">
                  {segment.customers.slice(0, 5).map((customer, custIndex) => (
                    <div key={custIndex} className="cust-segment-customer">
                      <div className="cust-segment-customer-info">
                        <Typography className="cust-segment-customer-name">{customer.name}</Typography>
                        <Typography className="cust-segment-customer-orders">
                          {customer.order_count} orders
                        </Typography>
                      </div>
                      <Typography className="cust-segment-customer-spent">
                        LKR {customer.total_spent?.toLocaleString()}
                      </Typography>
                    </div>
                  ))}
                  
                  {segment.customers.length > 5 && (
                    <Typography className="cust-segment-more">
                      +{segment.customers.length - 5} more customers
                    </Typography>
                  )}
                </div>

                {segment.customers.length > 0 && (
                  <div className="cust-segment-stats">
                    <div className="cust-segment-stat">
                      <Typography className="cust-segment-stat-label">Total Revenue</Typography>
                      <Typography className="cust-segment-stat-value">
                        LKR {segment.customers.reduce((sum, c) => sum + c.total_spent, 0).toLocaleString()}
                      </Typography>
                    </div>
                    <div className="cust-segment-stat">
                      <Typography className="cust-segment-stat-label">Avg Order Value</Typography>
                      <Typography className="cust-segment-stat-value">
                        LKR {(segment.customers.reduce((sum, c) => sum + c.total_spent, 0) / segment.customers.reduce((sum, c) => sum + c.order_count, 0)).toFixed(0)}
                      </Typography>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  const renderReportContent = () => {
    switch (reportType) {
      case 'customer_analytics':
        return renderCustomerAnalytics();
      case 'loyal_customers':
        return renderTopCustomers();
      case 'customer_segments':
        return renderCustomerSegments();
      default:
        return renderCustomerAnalytics();
    }
  };

  const currentReport = reportTypes.find(r => r.value === reportType);

  return (
    <Box className="cust-reports-container">
      {/* Header */}
      <Card className="cust-header-card">
        <CardContent className="cust-header-content">
          <div className="cust-header-main">
            <Button
              startIcon={<ArrowBack />}
              onClick={() => onPageChange('all-reports')}
              className="cust-back-button"
            >
              Back to Reports
            </Button>
            <div className="cust-header-text">
              <Typography className="cust-header-title">Customer Analytics</Typography>
              <Typography className="cust-header-subtitle">
                {currentReport?.description}
              </Typography>
            </div>
          </div>
          <div className="cust-header-actions">
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={loadReportData} 
                disabled={loading}
                className="cust-refresh-button"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<Download />}
              onClick={handleExport}
              disabled={!reportData || loading}
              className="cust-export-button"
            >
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Type Selection */}
      <Card className="cust-controls-card">
        <CardContent className="cust-controls-content">
          <Typography className="cust-controls-title">Report Type</Typography>
          <div className="cust-controls-grid">
            <FormControl fullWidth className="cust-select-control">
              <InputLabel>Select Report</InputLabel>
              <Select
                value={reportType}
                label="Select Report"
                onChange={(e) => setReportType(e.target.value)}
              >
                {reportTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <div className="cust-menu-item">
                      {type.icon}
                      {type.label}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DateRangeFilter
              filters={filters}
              onFilterChange={setFilters}
            />
          </div>
          {lastUpdated && (
            <div className="cust-last-updated">
              <Typography className="cust-updated-text">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert 
          severity="error" 
          className="cust-error-alert"
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={loadReportData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="cust-loading-container">
          <CircularProgress size={60} className="cust-loading-spinner" />
          <Typography className="cust-loading-text">
            Loading customer data...
          </Typography>
        </div>
      ) : reportData ? (
        renderReportContent()
      ) : (
        <Card className="cust-empty-card">
          <CardContent className="cust-empty-content">
            <Typography className="cust-empty-text">
              No customer data available for the selected report type.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CustomerReports;